import React, { useEffect, useMemo, useState } from 'react';
import Icon from '../../../components/AppIcon';
import { PAYMENT_FREQUENCIES, EMPLOYMENT_TYPES } from '../constants';
import { formatDateTimeLocalInput, safeCurrency, toFiniteNumber, toISOStringOrNull } from '../utils';

const WORKER_CATEGORIES = ['Permanent', 'Contract', 'Gig', 'Platform'];
const REVISION_REASONS = ['Increment', 'Promotion', 'Correction', 'Market Adjustment'];

const defaultFormState = {
  user: '',
  workerCategory: 'Permanent',
  designation: '',
  workLocationState: '',
  employmentType: 'Salaried',
  hourlyRate: '',
  perVisitRate: '',
  expectedHoursPerPeriod: '',
  expectedVisitsPerPeriod: '',
  basicSalary: '',
  allowances: {
    houseRent: '',
    medical: '',
    transport: '',
    food: '',
    dearness: '',
    special: '',
    other: ''
  },
  deductions: {
    tax: '',
    insurance: '',
    loan: '',
    other: ''
  },
  ctc: '',
  minimumWage: '',
  isMinimumWageCompliant: true,
  pf: {
    applicable: true,
    uan: '',
    employeeContribution: '',
    employerContribution: ''
  },
  esic: {
    applicable: false,
    number: '',
    employeeContribution: '',
    employerContribution: ''
  },
  gratuity: {
    applicable: false,
    yearsOfService: '',
    amountAccrued: ''
  },
  overtime: {
    eligible: false,
    rateMultiplier: '2'
  },
  paymentFrequency: 'Monthly',
  currency: 'INR',
  effectiveFrom: '',
  effectiveTo: '',
  isActive: true,
  revisionReason: '',
  remarks: ''
};

const SalaryFormModal = ({
  open,
  mode = 'create',
  initialData,
  employeeOptions = [],
  employeesLoading = false,
  onClose,
  onSubmit
}) => {
  const initialState = useMemo(() => {
    console.log('Computing initialState, initialData:', initialData);
    
    if (!initialData) {
      return defaultFormState;
    }
    
    // Determine employment type with fallback logic for existing records
    let employmentType = initialData.employmentType || 'Salaried';
    
    // If no employmentType but has hourlyRate, it's Hourly
    if (!initialData.employmentType && initialData.hourlyRate && initialData.hourlyRate > 0) {
      employmentType = 'Hourly';
    }
    // If no employmentType but has perVisitRate, it's Per-Visit
    else if (!initialData.employmentType && initialData.perVisitRate && initialData.perVisitRate > 0) {
      employmentType = 'Per-Visit';
    }
    // If has basicSalary, default to Salaried
    else if (!initialData.employmentType && initialData.basicSalary && initialData.basicSalary > 0) {
      employmentType = 'Salaried';
    }
    
    console.log('Determined employmentType:', employmentType);

    const computed = {
      user: initialData.user?._id || initialData.user || '',
      workerCategory: initialData.workerCategory || 'Permanent',
      designation: initialData.designation || '',
      workLocationState: initialData.workLocationState || '',
      employmentType: employmentType,
      hourlyRate: initialData.hourlyRate ? initialData.hourlyRate : '',
      perVisitRate: initialData.perVisitRate ? initialData.perVisitRate : '',
      expectedHoursPerPeriod: initialData.expectedHoursPerPeriod ? initialData.expectedHoursPerPeriod : '',
      expectedVisitsPerPeriod: initialData.expectedVisitsPerPeriod ? initialData.expectedVisitsPerPeriod : '',
      basicSalary: initialData.basicSalary ? initialData.basicSalary : '',
      allowances: {
        houseRent: initialData.allowances?.houseRent ?? '',
        medical: initialData.allowances?.medical ?? '',
        transport: initialData.allowances?.transport ?? '',
        food: initialData.allowances?.food ?? '',
        dearness: initialData.allowances?.dearness ?? '',
        special: initialData.allowances?.special ?? '',
        other: initialData.allowances?.other ?? ''
      },
      deductions: {
        tax: initialData.deductions?.tax ?? '',
        insurance: initialData.deductions?.insurance ?? '',
        loan: initialData.deductions?.loan ?? '',
        other: initialData.deductions?.other ?? ''
      },
      ctc: initialData.ctc ?? '',
      minimumWage: initialData.minimumWage ?? '',
      isMinimumWageCompliant: Boolean(initialData.isMinimumWageCompliant ?? true),
      pf: {
        applicable: Boolean(initialData.pf?.applicable ?? true),
        uan: initialData.pf?.uan || '',
        employeeContribution: initialData.pf?.employeeContribution ?? '',
        employerContribution: initialData.pf?.employerContribution ?? ''
      },
      esic: {
        applicable: Boolean(initialData.esic?.applicable),
        number: initialData.esic?.number || '',
        employeeContribution: initialData.esic?.employeeContribution ?? '',
        employerContribution: initialData.esic?.employerContribution ?? ''
      },
      gratuity: {
        applicable: Boolean(initialData.gratuity?.applicable),
        yearsOfService: initialData.gratuity?.yearsOfService ?? '',
        amountAccrued: initialData.gratuity?.amountAccrued ?? ''
      },
      overtime: {
        eligible: Boolean(initialData.overtime?.eligible),
        rateMultiplier: initialData.overtime?.rateMultiplier ?? '2'
      },
      paymentFrequency: initialData.paymentFrequency || 'Monthly',
      currency: initialData.currency || 'INR',
      effectiveFrom: formatDateTimeLocalInput(initialData.effectiveFrom) || '',
      effectiveTo: formatDateTimeLocalInput(initialData.effectiveTo) || '',
      isActive: Boolean(initialData.isActive),
      revisionReason: initialData.revisionReason || '',
      remarks: initialData.remarks || ''
    };
    
    console.log('Computed form state:', computed);
    return computed;
  }, [initialData]);

  const [form, setForm] = useState(initialState);
  const [submitting, setSubmitting] = useState(false);
  const [validationError, setValidationError] = useState(null);

  useEffect(() => {
    if (open) {
      setForm(initialState);
      setValidationError(null);
    }
  }, [initialState, open]);


  const handleAutoCalculate = () => {
    const basicSalary = parseFloat(form.basicSalary) || 0;

    if (basicSalary <= 0) {
      alert('Please enter a valid basic salary first');
      return;
    }

    // Calculate allowances (Labour Code compliant percentages)
    const houseRent = (basicSalary * 0.40).toFixed(2);  // 40% HRA
    const dearness = (basicSalary * 0.10).toFixed(2);   // 10% DA

    // Calculate current total allowances
    const currentAllowances =
      parseFloat(form.allowances.medical || 0) +
      parseFloat(form.allowances.transport || 0) +
      parseFloat(form.allowances.food || 0) +
      parseFloat(form.allowances.special || 0) +
      parseFloat(form.allowances.other || 0);

    const gross = basicSalary + parseFloat(houseRent) + parseFloat(dearness) + currentAllowances;

    // Calculate PF (12% each from employee and employer)
    const pfEmployeeContribution = (basicSalary * 0.12).toFixed(2);
    const pfEmployerContribution = (basicSalary * 0.12).toFixed(2);

    // Calculate ESIC (applicable only if gross <= 21000)
    let esicUpdates = { ...form.esic };
    if (gross <= 21000 && form.esic.applicable) {
      esicUpdates.employeeContribution = (gross * 0.0075).toFixed(2);  // 0.75%
      esicUpdates.employerContribution = (gross * 0.0325).toFixed(2);   // 3.25%
    }

    // Calculate CTC (Basic + Allowances + Employer contributions)
    const employerPFContribution = parseFloat(pfEmployerContribution);
    const employerESICContribution = form.esic.applicable && gross <= 21000 
      ? parseFloat(esicUpdates.employerContribution) 
      : 0;
    const ctc = (gross + employerPFContribution + employerESICContribution).toFixed(2);

    // Update form with calculated values
    setForm(prev => ({
      ...prev,
      allowances: {
        ...prev.allowances,
        houseRent,
        dearness
      },
      pf: {
        ...prev.pf,
        employeeContribution: pfEmployeeContribution,
        employerContribution: pfEmployerContribution
      },
      esic: esicUpdates,
      ctc
    }));
  };

  if (!open) {
    return null;
  }

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleNestedChange = (section, field, value) => {
    setForm((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setValidationError(null);
    
    // Validate based on employment type
    if (!form.user || !form.effectiveFrom || !form.workLocationState) {
      setValidationError('Please fill all required fields: Employee, Work Location State, and Effective From');
      return;
    }

    if (form.employmentType === 'Salaried') {
      if (!form.basicSalary || parseFloat(form.basicSalary) <= 0 || !form.ctc || !form.minimumWage) {
        setValidationError('Please fill all required fields for Salaried employees: Basic Salary, CTC, and Minimum Wage');
        return;
      }

      // Labour Code validation: Basic salary must be >= 50% of gross
      const grossSalary = parseFloat(form.basicSalary) + 
        parseFloat(form.allowances.houseRent || 0) +
        parseFloat(form.allowances.medical || 0) +
        parseFloat(form.allowances.transport || 0) +
        parseFloat(form.allowances.food || 0) +
        parseFloat(form.allowances.dearness || 0) +
        parseFloat(form.allowances.special || 0) +
        parseFloat(form.allowances.other || 0);
      
      if (parseFloat(form.basicSalary) < grossSalary * 0.5) {
        const minRequired = (grossSalary * 0.5).toFixed(2);
        setValidationError(
          'Labour Code Violation: Basic salary must be at least 50% of total wages.\nCurrent: Rs.' + form.basicSalary + ' | Required: Rs.' + minRequired + ' | Total Wages: Rs.' + grossSalary.toFixed(2)
        );
        return;
      }
    } else if (form.employmentType === 'Hourly') {
      if (!form.hourlyRate || parseFloat(form.hourlyRate) <= 0) {
        setValidationError('Please enter a valid hourly rate for Hourly employment type');
        return;
      }
    } else if (form.employmentType === 'Per-Visit') {
      if (!form.perVisitRate || parseFloat(form.perVisitRate) <= 0) {
        setValidationError('Please enter a valid per-visit rate for Per-Visit employment type');
        return;
      }
    }

    const payload = {
      user: form.user,
      workerCategory: form.workerCategory,
      designation: form.designation?.trim() || undefined,
      workLocationState: form.workLocationState?.trim(),
      employmentType: form.employmentType,
      hourlyRate: toFiniteNumber(form.hourlyRate),
      perVisitRate: toFiniteNumber(form.perVisitRate),
      expectedHoursPerPeriod: toFiniteNumber(form.expectedHoursPerPeriod),
      expectedVisitsPerPeriod: toFiniteNumber(form.expectedVisitsPerPeriod),
      basicSalary: toFiniteNumber(form.basicSalary, 0),
      allowances: {
        houseRent: toFiniteNumber(form.allowances.houseRent),
        medical: toFiniteNumber(form.allowances.medical),
        transport: toFiniteNumber(form.allowances.transport),
        food: toFiniteNumber(form.allowances.food),
        dearness: toFiniteNumber(form.allowances.dearness),
        special: toFiniteNumber(form.allowances.special),
        other: toFiniteNumber(form.allowances.other)
      },
      deductions: {
        tax: toFiniteNumber(form.deductions.tax),
        insurance: toFiniteNumber(form.deductions.insurance),
        loan: toFiniteNumber(form.deductions.loan),
        other: toFiniteNumber(form.deductions.other)
      },
      ctc: toFiniteNumber(form.ctc, 0),
      minimumWage: toFiniteNumber(form.minimumWage, 0),
      isMinimumWageCompliant: form.isMinimumWageCompliant,
      pf: {
        applicable: form.pf.applicable,
        uan: form.pf.uan?.trim() || undefined,
        employeeContribution: toFiniteNumber(form.pf.employeeContribution),
        employerContribution: toFiniteNumber(form.pf.employerContribution)
      },
      esic: {
        applicable: form.esic.applicable,
        number: form.esic.number?.trim() || undefined,
        employeeContribution: toFiniteNumber(form.esic.employeeContribution),
        employerContribution: toFiniteNumber(form.esic.employerContribution)
      },
      gratuity: {
        applicable: form.gratuity.applicable,
        yearsOfService: toFiniteNumber(form.gratuity.yearsOfService),
        amountAccrued: toFiniteNumber(form.gratuity.amountAccrued)
      },
      overtime: {
        eligible: form.overtime.eligible,
        rateMultiplier: toFiniteNumber(form.overtime.rateMultiplier, 2)
      },
      paymentFrequency: form.paymentFrequency,
      currency: safeCurrency(form.currency || 'INR'),
      effectiveFrom: toISOStringOrNull(form.effectiveFrom),
      effectiveTo: toISOStringOrNull(form.effectiveTo),
      revisionReason: form.revisionReason || undefined,
      remarks: form.remarks?.trim() || undefined
    };

    if (mode === 'edit') {
      payload.isActive = form.isActive;
    }

    setSubmitting(true);
    try {
      const success = await onSubmit?.(payload);
      if (success) {
        onClose?.();
      }
    } catch (error) {
      // Capture any error from parent component
      const errorMessage = error?.message || error?.error || 'An error occurred';
      setValidationError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const renderMoneyInput = (section, field, label) => (
    <div key={`${section}-${field}`}>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <input
        type="number"
        step="0.01"
        min="0"
        value={form[section][field]}
        onChange={(event) => handleNestedChange(section, field, event.target.value)}
        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[95vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              {mode === 'edit' ? 'Update Salary' : 'Add Salary Profile'}
            </h3>
            <p className="text-sm text-gray-500">Configure structured pay and deductions</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <Icon name="X" size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-6 space-y-6">
          {/* Validation Error Display */}
          {validationError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-start">
                <Icon name="AlertCircle" size={20} className="text-red-600 mt-0.5 mr-3 flex-shrink-0" />
                <div>
                  <h4 className="text-sm font-semibold text-red-800 mb-1">Validation Error</h4>
                  <p className="text-sm text-red-700 whitespace-pre-line">{validationError}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setValidationError(null)}
                  className="ml-auto text-red-400 hover:text-red-600"
                >
                  <Icon name="X" size={16} />
                </button>
              </div>
            </div>
          )}

          {/* Basic Information */}
          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-3">Basic Information</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Employee *</label>
                <select
                  name="user"
                  value={form.user}
                  onChange={handleChange}
                  disabled={mode === 'edit' || employeesLoading}
                  required
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                >
                  <option value="">
                    {employeesLoading ? 'Loading employees...' : 'Select employee'}
                  </option>
                  {employeeOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Worker Category *</label>
                <select
                  name="workerCategory"
                  value={form.workerCategory}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                >
                  {WORKER_CATEGORIES.map((category) => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Employment Type *</label>
                <select
                  name="employmentType"
                  value={form.employmentType}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                >
                  {EMPLOYMENT_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Designation</label>
                <input
                  type="text"
                  name="designation"
                  value={form.designation}
                  onChange={handleChange}
                  maxLength={100}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  placeholder="e.g., Senior Manager"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Work Location State *</label>
                <input
                  type="text"
                  name="workLocationState"
                  value={form.workLocationState}
                  onChange={handleChange}
                  required
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  placeholder="e.g., Maharashtra"
                />
              </div>
            </div>
          </div>

          {/* Salary Structure */}
          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-3">
              {form.employmentType === 'Salaried' ? 'Salary Structure' : 'Compensation Structure'}
            </h4>
            
            {form.employmentType === 'Salaried' && (
              <>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 text-sm text-blue-800">
                  <div className="flex items-start">
                    <Icon name="Info" size={16} className="mr-2 mt-0.5 flex-shrink-0" />
                    <div>
                      <strong>Labour Code Requirement:</strong> Basic salary must be at least 50% of total wages to ensure proper PF, gratuity, and overtime calculations.
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Basic Salary *</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      name="basicSalary"
                      value={form.basicSalary}
                      onChange={handleChange}
                      required
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">CTC (Cost to Company) *</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      name="ctc"
                      value={form.ctc}
                      onChange={handleChange}
                      required
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Minimum Wage *</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      name="minimumWage"
                      value={form.minimumWage}
                      onChange={handleChange}
                      required
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    />
                  </div>
                  <div className="flex items-center mt-6">
                    <label className="inline-flex items-center text-sm text-gray-700">
                      <input
                        type="checkbox"
                        name="isMinimumWageCompliant"
                        checked={form.isMinimumWageCompliant}
                        onChange={(event) => setForm((prev) => ({ ...prev, isMinimumWageCompliant: event.target.checked }))}
                        className="rounded border-gray-300 text-blue-600"
                      />
                      <span className="ml-2">Minimum Wage Compliant</span>
                    </label>
                  </div>
                  <div className="md:col-span-2">
                    <button
                      type="button"
                      onClick={handleAutoCalculate}
                      className="w-full inline-flex items-center justify-center px-3 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors"
                    >
                      <Icon name="Calculator" size={16} className="mr-2" />
                      Auto Calculate Allowances, PF, ESIC & CTC
                    </button>
                  </div>
                </div>
              </>
            )}

            {form.employmentType === 'Hourly' && (
              <>
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4 text-sm text-green-800">
                  <div className="flex items-start">
                    <Icon name="Clock" size={16} className="mr-2 mt-0.5 flex-shrink-0" />
                    <div>
                      <strong>Hourly Rate:</strong> Set the hourly rate for freelance/contract workers. Compensation will be calculated based on hours worked.
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Hourly Rate ({form.currency}) *</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      name="hourlyRate"
                      value={form.hourlyRate}
                      onChange={handleChange}
                      required
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                      placeholder="e.g., 500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Expected Hours Per Period</label>
                    <input
                      type="number"
                      min="0"
                      step="0.5"
                      name="expectedHoursPerPeriod"
                      value={form.expectedHoursPerPeriod}
                      onChange={handleChange}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                      placeholder="e.g., 160"
                    />
                    <p className="text-xs text-gray-500 mt-1">Estimated hours per payment period for budgeting</p>
                  </div>
                </div>
              </>
            )}

            {form.employmentType === 'Per-Visit' && (
              <>
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 mb-4 text-sm text-purple-800">
                  <div className="flex items-start">
                    <Icon name="UserCheck" size={16} className="mr-2 mt-0.5 flex-shrink-0" />
                    <div>
                      <strong>Per-Visit Rate:</strong> Set the rate per visit for doctors/consultants. Compensation will be calculated based on number of visits.
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Per-Visit Rate ({form.currency}) *</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      name="perVisitRate"
                      value={form.perVisitRate}
                      onChange={handleChange}
                      required
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                      placeholder="e.g., 2000"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Expected Visits Per Period</label>
                    <input
                      type="number"
                      min="0"
                      step="1"
                      name="expectedVisitsPerPeriod"
                      value={form.expectedVisitsPerPeriod}
                      onChange={handleChange}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                      placeholder="e.g., 50"
                    />
                    <p className="text-xs text-gray-500 mt-1">Estimated visits per payment period for budgeting</p>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Allowances - Only for Salaried employees */}
          {form.employmentType === 'Salaried' && (
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-3">Allowances</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {renderMoneyInput('allowances', 'houseRent', 'House Rent')}
                {renderMoneyInput('allowances', 'medical', 'Medical')}
                {renderMoneyInput('allowances', 'transport', 'Transport')}
                {renderMoneyInput('allowances', 'food', 'Food')}
                {renderMoneyInput('allowances', 'dearness', 'Dearness Allow.')}
                {renderMoneyInput('allowances', 'special', 'Special Allow.')}
                {renderMoneyInput('allowances', 'other', 'Other')}
              </div>
            </div>
          )}

          {/* Deductions */}
          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-3">Deductions</h4>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {renderMoneyInput('deductions', 'tax', 'Tax')}
              {renderMoneyInput('deductions', 'insurance', 'Insurance')}
              {renderMoneyInput('deductions', 'loan', 'Loan')}
              {renderMoneyInput('deductions', 'other', 'Other')}
            </div>
          </div>

          {/* Provident Fund - Only for Salaried employees */}
          {form.employmentType === 'Salaried' && (
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-3">Provident Fund (PF)</h4>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="md:col-span-4">
                  <label className="inline-flex items-center text-sm text-gray-700 mb-2">
                    <input
                      type="checkbox"
                      checked={form.pf.applicable}
                      onChange={(event) => handleNestedChange('pf', 'applicable', event.target.checked)}
                      className="rounded border-gray-300 text-blue-600 mr-2"
                    />
                    PF Applicable
                  </label>
                </div>
                {form.pf.applicable && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">UAN Number</label>
                      <input
                        type="text"
                        value={form.pf.uan}
                        onChange={(event) => handleNestedChange('pf', 'uan', event.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2"
                        placeholder="UAN number"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Employee Contribution</label>
                      <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={form.pf.employeeContribution}
                      onChange={(event) => handleNestedChange('pf', 'employeeContribution', event.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Employer Contribution</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={form.pf.employerContribution}
                      onChange={(event) => handleNestedChange('pf', 'employerContribution', event.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    />
                  </div>
                </>
              )}
            </div>
          </div>
          )}

          {/* ESIC - Only for Salaried employees */}
          {form.employmentType === 'Salaried' && (
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-3">ESIC (Employee State Insurance)</h4>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="md:col-span-4">
                  <label className="inline-flex items-center text-sm text-gray-700 mb-2">
                    <input
                      type="checkbox"
                      checked={form.esic.applicable}
                      onChange={(event) => handleNestedChange('esic', 'applicable', event.target.checked)}
                      className="rounded border-gray-300 text-blue-600 mr-2"
                    />
                    ESIC Applicable (for gross salary ≤ ₹21,000)
                  </label>
                </div>
                {form.esic.applicable && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">ESIC Number</label>
                      <input
                        type="text"
                        value={form.esic.number}
                        onChange={(event) => handleNestedChange('esic', 'number', event.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2"
                        placeholder="ESIC number"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Employee Contribution (0.75%)</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={form.esic.employeeContribution}
                        onChange={(event) => handleNestedChange('esic', 'employeeContribution', event.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Employer Contribution (3.25%)</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={form.esic.employerContribution}
                        onChange={(event) => handleNestedChange('esic', 'employerContribution', event.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2"
                      />
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Gratuity - Only for Salaried employees */}
          {form.employmentType === 'Salaried' && (
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-3">Gratuity</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="md:col-span-3">
                  <label className="inline-flex items-center text-sm text-gray-700 mb-2">
                    <input
                      type="checkbox"
                      checked={form.gratuity.applicable}
                      onChange={(event) => handleNestedChange('gratuity', 'applicable', event.target.checked)}
                      className="rounded border-gray-300 text-blue-600 mr-2"
                    />
                    Gratuity Applicable
                  </label>
                </div>
                {form.gratuity.applicable && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Years of Service</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={form.gratuity.yearsOfService}
                        onChange={(event) => handleNestedChange('gratuity', 'yearsOfService', event.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Amount Accrued</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={form.gratuity.amountAccrued}
                        onChange={(event) => handleNestedChange('gratuity', 'amountAccrued', event.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2"
                      />
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Overtime - Only for Salaried employees */}
          {form.employmentType === 'Salaried' && (
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-3">Overtime</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="inline-flex items-center text-sm text-gray-700 mb-2">
                    <input
                      type="checkbox"
                      checked={form.overtime.eligible}
                      onChange={(event) => handleNestedChange('overtime', 'eligible', event.target.checked)}
                      className="rounded border-gray-300 text-blue-600 mr-2"
                    />
                    Overtime Eligible
                  </label>
                </div>
                {form.overtime.eligible && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Rate Multiplier (Labour Code: 2x)</label>
                    <input
                      type="number"
                      step="0.1"
                      min="1"
                      value={form.overtime.rateMultiplier}
                      onChange={(event) => handleNestedChange('overtime', 'rateMultiplier', event.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Payment Details & Effective Period */}
          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-3">Payment Details & Effective Period</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Effective From *</label>
                <input
                  type="datetime-local"
                  name="effectiveFrom"
                  value={form.effectiveFrom}
                  onChange={handleChange}
                  required
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Effective To</label>
                <input
                  type="datetime-local"
                  name="effectiveTo"
                  value={form.effectiveTo}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Currency *</label>
                <input
                  type="text"
                  name="currency"
                  value={form.currency}
                  onChange={handleChange}
                  maxLength={3}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 uppercase"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Payment Frequency *</label>
                <select
                  name="paymentFrequency"
                  value={form.paymentFrequency}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                >
                  {PAYMENT_FREQUENCIES.map((freq) => (
                    <option key={freq} value={freq}>
                      {freq}
                    </option>
                  ))}
                </select>
              </div>
              {mode === 'edit' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Revision Reason</label>
                    <select
                      name="revisionReason"
                      value={form.revisionReason}
                      onChange={handleChange}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    >
                      <option value="">Select reason</option>
                      {REVISION_REASONS.map((reason) => (
                        <option key={reason} value={reason}>
                          {reason}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex items-center mt-6">
                    <label className="inline-flex items-center text-sm text-gray-700">
                      <input
                        type="checkbox"
                        name="isActive"
                        checked={form.isActive}
                        onChange={(event) => setForm((prev) => ({ ...prev, isActive: event.target.checked }))}
                        className="rounded border-gray-300 text-blue-600"
                      />
                      <span className="ml-2">Active salary</span>
                    </label>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Remarks */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Remarks</label>
            <textarea
              name="remarks"
              value={form.remarks}
              onChange={handleChange}
              rows={3}
              maxLength={1000}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
              placeholder="Add any additional context (max 1000 characters)"
            />
          </div>

          {/* Submit Buttons */}
          <div className="flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-5 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
            >
              {submitting ? 'Saving...' : mode === 'edit' ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SalaryFormModal;
